import { api } from '../api';
import { useLoad, usePolling } from '../hooks';
import { duration, eventLabel, itemCount, stamp, timeAgo } from '../format';
import { Card, Empty, ErrorNote, Loading, StatusPill, Table } from '../components/ui';
import { useNavigate } from '../router';
import type { OcrResultItem, Page, ResourceEvent, RunDetail as Run } from '../types';

/**
 * Bir çalışma, uçtan uca — hangi kimliği ve hangi proxy'yi ödünç aldığı dahil.
 *
 * Bu atıf, kiralama defterinin tüm varlık sebebi: "X çalışması patladı" aksiyona
 * dönüşmez, ama "X çalışması IG_03 hesabıyla NL-2 proxy'si üzerinden patladı" dönüşür.
 */
export function RunDetail({ runId }: { runId: string }) {
  const navigate = useNavigate();
  const run = useLoad(() => api.get<Run>(`/v1/runs/${runId}`), [runId]);
  // Düz dizi döner.
  const events = useLoad(() => api.get<ResourceEvent[]>('/v1/resources/events?limit=50'), [runId]);

  const live = run.data?.status === 'RUNNING' || run.data?.status === 'QUEUED';
  usePolling(run.reload, 5_000, live);

  const stats = run.data?.stats ?? {};
  const identityResourceId =
    typeof stats.identityResourceId === 'string' ? stats.identityResourceId : null;
  const proxyResourceId = typeof stats.proxyResourceId === 'string' ? stats.proxyResourceId : null;
  const relevant = (events.data ?? []).filter(
    (event) =>
      event.resourceId !== null &&
      (event.resourceId === identityResourceId || event.resourceId === proxyResourceId),
  );

  const SESSION_TR: Record<string, string> = {
    login: 'parola ile giriş',
    restored: 'kayıtlı oturum geri yüklendi',
    reused: 'açık oturum yeniden kullanıldı',
  };

  return (
    <>
      <Card
        title="Çalışma"
        actions={
          <button type="button" className="ghost tiny" onClick={() => navigate('/runs')}>
            Geri
          </button>
        }
        flush
      >
        {run.error && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={run.error} />
          </div>
        )}
        {run.loading && !run.data && <Loading />}
        {run.data && (
          <dl className="kv">
            <dt>Kimlik</dt>
            <dd className="mono">{run.data.id}</dd>
            <dt>Actor</dt>
            <dd>
              <span className="cell-title">{run.data.actorSlug}</span>{' '}
              <span className="muted mono">{run.data.actorVersion}</span>
            </dd>
            <dt>Durum</dt>
            <dd>
              <StatusPill status={run.data.status} />
              {run.data.errorCode && <span className="muted mono"> · {run.data.errorCode}</span>}
            </dd>
            {run.data.errorMessage && (
              <>
                <dt>Hata</dt>
                <dd className="error">{run.data.errorMessage}</dd>
              </>
            )}
            <dt>Deneme</dt>
            <dd>
              {run.data.maxAttempts} içinde {run.data.attempt}
            </dd>
            <dt>Öğe sayısı</dt>
            <dd>{itemCount(stats)}</dd>
            <dt>Başlangıç</dt>
            <dd title={stamp(run.data.startedAt)}>{timeAgo(run.data.startedAt)}</dd>
            <dt>Bitiş</dt>
            <dd title={stamp(run.data.finishedAt)}>{timeAgo(run.data.finishedAt)}</dd>
            <dt>Süre</dt>
            <dd>{duration(typeof stats.durationMs === 'number' ? stats.durationMs : null)}</dd>
          </dl>
        )}
      </Card>

      {run.data && (
        <Card title="Kullanılan kaynaklar" flush>
          {!identityResourceId && !proxyResourceId && (
            <Empty title="Yönetilen kaynak kullanılmadı">
              Bu çalışma havuzdan kimlik ya da proxy ödünç almadı.
            </Empty>
          )}
          {(identityResourceId ?? proxyResourceId) && (
            <dl className="kv">
              {identityResourceId && (
                <>
                  <dt>Kimlik</dt>
                  <dd className="mono">
                    {typeof stats.identityLabel === 'string'
                      ? stats.identityLabel
                      : identityResourceId}
                  </dd>
                </>
              )}
              {typeof stats.sessionAction === 'string' && (
                <>
                  <dt>Oturum</dt>
                  <dd>{SESSION_TR[stats.sessionAction] ?? stats.sessionAction}</dd>
                </>
              )}
              {proxyResourceId && (
                <>
                  <dt>Proxy</dt>
                  <dd className="mono">
                    {typeof stats.proxySafeUrl === 'string' ? stats.proxySafeUrl : proxyResourceId}
                  </dd>
                </>
              )}
              {typeof stats.upstreamRequests === 'number' && (
                <>
                  <dt>Instagram isteği</dt>
                  <dd>{stats.upstreamRequests}</dd>
                </>
              )}
            </dl>
          )}
          {relevant.length > 0 && (
            <>
              <h4>Bu çalışma çevresindeki kaynak olayları</h4>
              <Table head={['Ne zaman', 'Olay', 'Ayrıntı']}>
                {relevant.map((event) => (
                  <tr key={event.id}>
                    <td className="muted" title={stamp(event.createdAt)}>
                      {timeAgo(event.createdAt)}
                    </td>
                    <td className="cell-title">{eventLabel(event.type)}</td>
                    <td className="muted">{event.message ?? '—'}</td>
                  </tr>
                ))}
              </Table>
            </>
          )}
        </Card>
      )}

      {run.data && (
        <Card title="Girdi">
          <pre className="code">{JSON.stringify(run.data.input, null, 2)}</pre>
        </Card>
      )}

      {run.data?.datasetId && <RunDataset runId={runId} />}
    </>
  );
}

function RunDataset({ runId }: { runId: string }) {
  const items = useLoad(
    () => api.get<Page<{ id: string; data: unknown }>>(`/v1/runs/${runId}/dataset?limit=10`),
    [runId],
  );

  const ids = (items.data?.items ?? []).map((item) => item.id);

  return (
    <>
      <VisionResults ids={ids} loading={items.loading} />

      <Card title="Veri kümesi (ilk 10)">
        <ErrorNote message={items.error} />
        {items.loading && <Loading />}
        {(items.data?.items.length ?? 0) === 0 && !items.loading && (
          <Empty title="Öğe yok">Bu çalışma veri kümesine bir şey yazmadı.</Empty>
        )}
        {(items.data?.items ?? []).map((item) => (
          <pre className="code" key={item.id}>
            {JSON.stringify(item.data, null, 2)}
          </pre>
        ))}
      </Card>
    </>
  );
}

/**
 * Modelin görselden ne okuduğu.
 *
 * Panelde bu yoktu: harcama görünüyordu ama karşılığında ne alındığı görünmüyordu.
 * Kalite sorusunun ("afişi doğru okudu mu, tarihi doğru çıkardı mı") tek cevabı burası —
 * ve o soru, modeli ya da istemi değiştirip değiştirmemeye karar verirken sorulan soru.
 *
 * Ham JSON olduğu gibi gösteriliyor, biçimlendirilmiyor: içindeki alanların ne anlama
 * geldiğini istemi yazan bilir, Rab-bit bilmez. Alanları isimleriyle göstermek, çekirdeğe
 * o şemayı öğretmek olurdu.
 */
function VisionResults({ ids, loading }: { ids: string[]; loading: boolean }) {
  const key = ids.join(',');
  const results = useLoad(
    () =>
      key
        ? api.get<OcrResultItem[]>(`/v1/ocr/results?datasetItemIds=${key}`)
        : Promise.resolve([] as OcrResultItem[]),
    [key],
  );

  if (loading || ids.length === 0) return null;
  const rows = results.data ?? [];

  return (
    <Card title={`Görsel analizi (${rows.length})`} flush>
      <div style={{ padding: '14px 16px 0' }}>
        <ErrorNote message={results.error} />
      </div>
      {results.loading && rows.length === 0 && <Loading />}
      {!results.loading && rows.length === 0 && (
        <Empty title="Bu çalışmada görsel işlenmedi.">
          Ayarlardaki mod kapalıysa ya da gönderilerde işlenebilir görsel yoksa bu
          normaldir.
        </Empty>
      )}
      {rows.map((row) => (
        <div className="vision-row" key={`${row.datasetItemId}-${row.imageRef}`}>
          <div className="vision-head">
            <StatusPill status={row.status} />
            <span className="mono muted">{row.imageRef}</span>
            <span className="muted small">{row.imageRole}</span>
            {row.engineVersion && <span className="mono muted small">{row.engineVersion}</span>}
            {row.cacheHit && <span className="pill pill-succeeded">önbellek</span>}
            <span className="muted small">{duration(row.durationMs)}</span>
            {row.errorCode && <span className="error mono small">{row.errorCode}</span>}
          </div>

          {row.text && (
            <>
              <h4>Okunan metin</h4>
              <pre className="code">{row.text}</pre>
            </>
          )}
          {row.data !== null && row.data !== undefined && (
            <>
              <h4>Modelin çıktısı</h4>
              <pre className="code">{JSON.stringify(row.data, null, 2)}</pre>
            </>
          )}
        </div>
      ))}
    </Card>
  );
}
